import { useState } from "react";
import { Telescope } from "lucide-react";
import { api, errMsg, PLANET_GLYPHS, PLANET_EN_ES, SIGN_EN_ES, ZODIAC_GLYPHS, ASPECT_EN_ES, TARGET_ES } from "../lib/api";
import { useContent } from "../lib/content";
import TransitInfo from "./TransitInfo";
import TransitTimeline from "./TransitTimeline";

const INTENSITY_CLS = { "Muy intensa": "text-[#C9A25E] border-[#C9A25E]/60", Intensa: "text-amber-200/80 border-amber-200/30", Suave: "text-zinc-500 border-white/10" };

function TransitRow({ t }) {
  return (
    <div data-testid={`transit-${t.planet.toLowerCase()}`} className="card !p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <span className="text-[#C9A25E] text-2xl">{PLANET_GLYPHS[t.planet]}</span>
          <span className="font-display text-2xl text-white">{PLANET_EN_ES[t.planet]}</span>
          {t.retrograde && <span className="text-xs text-zinc-500 border border-white/10 px-2 py-0.5">℞ retrógrado</span>}
        </div>
        <div className="text-sm text-zinc-300">
          <span className="text-[#C9A25E] mr-1">{ZODIAC_GLYPHS[t.sign]}</span>
          {SIGN_EN_ES[t.sign]} {Math.floor(t.degree)}° · <span className="text-white">Casa {t.house}</span>
        </div>
      </div>
      <p className="text-xs text-zinc-500 mb-4">
        {Math.floor(t.house_degree)}° dentro de la casa ·{" "}
        <span data-testid={`transit-intensity-${t.planet.toLowerCase()}`} className={`border px-2 py-0.5 ${INTENSITY_CLS[t.house_intensity]}`}>{t.house_intensity}</span>
      </p>
      {t.aspects.length ? (
        <ul className="space-y-1.5">
          {t.aspects.map((a) => (
            <li key={a.target + a.aspect} className="text-sm text-zinc-400 flex flex-wrap gap-x-2">
              <span className="text-[#C9A25E]">{ASPECT_EN_ES[a.aspect]}</span>
              <span>a tu</span><span className="text-white">{TARGET_ES[a.target]}</span>
              <span className="text-zinc-600">· orbe {a.orb}° · {a.applying ? "aplicativo" : "separativo"}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-zinc-600">Sin aspectos dentro de orbe a planetas personales ni ejes ahora mismo.</p>
      )}
    </div>
  );
}

export default function TransitsPanel() {
  const { t } = useContent();
  const [data, setData] = useState(null);
  const [state, setState] = useState("closed");
  const [error, setError] = useState(null);

  const open = () => {
    setState("loading");
    Promise.all([api.get("/me/transits"), api.get("/platform/info").catch(() => ({ data: { items: [] } }))])
      .then(([tr, info]) => { setData({ ...tr.data, info: info.data.items }); setState("open"); })
      .catch((e) => { setError(errMsg(e)); setState("closed"); });
  };

  if (state !== "open") {
    return (
      <div>
        <button data-testid="transits-open" onClick={open} disabled={state === "loading"} className="btn-lilac inline-flex items-center gap-2">
          <Telescope size={16} /> {state === "loading" ? "Calculando el cielo…" : t("plat_transits_button", "Ver mis tránsitos")}
        </button>
        {error && <p className="text-zinc-500 mt-4">{error}</p>}
      </div>
    );
  }
  return (
    <div>
      <TransitInfo items={data.info} />
      <h3 className="font-display text-3xl text-white font-light mb-2">{t("plat_transits_now_title", "Tránsitos de hoy")}</h3>
      <p data-testid="transits-date" className="text-zinc-500 text-sm mb-6">
        {new Date(data.date).toLocaleDateString("es", { day: "numeric", month: "long", year: "numeric" })} ·{" "}
        {t("plat_transits_now_intro", "")}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {data.transits.map((tr) => <TransitRow key={tr.planet} t={tr} />)}
      </div>
      <TransitTimeline timeline={data.timeline} />
    </div>
  );
}
